import { differenceInSeconds, format } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { UTC_STRING_TIME_FORMAT } from 'global/appConstants';
import { getTimeRoundingInfo } from 'utils/helpers';
import toaster from 'utils/toaster';

export const secondsToTime = (timeInSeconds: number, source: string): string => {
  if (timeInSeconds < 0) return '00:00:00';

  const hour = Math.floor(timeInSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((timeInSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(timeInSeconds % 60)
    .toString()
    .padStart(2, '0');

  if (hour === '00' && source !== 'stopwatch') {
    return minutes + ':' + seconds;
  }
  return hour + ':' + minutes + ':' + seconds;
};

export const remainderTimeInSeconds = (item: any): number => {
  const breakStartTime = item?.breakStart ? new Date(item?.breakStart) : null;
  const breakStartTimezone = item?.timezone;
  const breakDurationInSeconds = item?.breakLength;
  if (!breakStartTime || !breakStartTimezone) return 0;
  const breakStartTimeToUtc = fromZonedTime(breakStartTime, breakStartTimezone);
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localBreakStartTime = toZonedTime(breakStartTimeToUtc, localTimezone);
  const currentTime = new Date();
  const remainingTime = breakDurationInSeconds - differenceInSeconds(currentTime, localBreakStartTime);
  return remainingTime;
};

export const getLocalTime = (time: Date, userTimezone: string) => {
  const utcTime = fromZonedTime(time, userTimezone);
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return toZonedTime(utcTime, localTimezone);
};

export const getZonedTime = () => {
  // const companyInfo = useCompanyInfo();
  // const zonedTimezone = companyInfo?.timezone;
  // const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // const localToUtc = fromZonedTime(time, localTimezone);
  // const zonedTime = toZonedTime(localToUtc, zonedTimezone);
  const fZonedTime = new Date();
  const hours = fZonedTime.getHours();
  const minutes = fZonedTime.getMinutes();
  return { hours, minutes };
};

export const getRoundUpTime = (type: 'clock_in' | 'clock_out') => {
  const timeRoundingInfo = getTimeRoundingInfo();
  let roundingFactor;
  let roundingType;
  if (type === 'clock_in') {
    roundingType = timeRoundingInfo?.clock_in_rounding_direction;
    roundingFactor = timeRoundingInfo?.clock_in_increment;
    const { roundedHours, roundedMinutes } = rounding(
      roundingFactor,
      roundingType,
      timeRoundingInfo?.clock_in_rounding
    );

    const currentDate = new Date();
    currentDate.setHours(roundedHours, roundedMinutes);
    return format(currentDate, UTC_STRING_TIME_FORMAT);
  }
  roundingType = timeRoundingInfo?.clock_out_rounding_direction;
  roundingFactor = timeRoundingInfo?.clock_out_increment;
  const { roundedHours, roundedMinutes } = rounding(roundingFactor, roundingType, timeRoundingInfo?.clock_out_rounding);

  const currentDate = new Date();
  currentDate.setHours(roundedHours, roundedMinutes);
  return format(currentDate, UTC_STRING_TIME_FORMAT);
};

const rounding = (roundingFactor: number, roundingType: 'up' | 'down' | 'nearest', toRound: boolean) => {
  // eslint-disable-next-line prefer-const
  let { hours, minutes } = getZonedTime();

  if (!toRound || roundingFactor === 0 || minutes % roundingFactor === 0) {
    return { roundedHours: +timePad(hours), roundedMinutes: +timePad(minutes) };
  }

  let h, m;

  switch (roundingType) {
    case 'up':
      const upRoundedNumber = Math.ceil(minutes / roundingFactor) * roundingFactor;
      m = upRoundedNumber === 60 ? 0 : upRoundedNumber;
      h = upRoundedNumber === 60 ? (hours === 23 ? 0 : ++hours) : hours;
      break;
    case 'down':
      m = Math.floor(minutes / roundingFactor) * roundingFactor;
      h = hours;
      break;
    case 'nearest':
    default:
      const nearestRoundedNumber = Math.round(minutes / roundingFactor) * roundingFactor;
      m = nearestRoundedNumber === 60 ? 0 : nearestRoundedNumber;
      h = nearestRoundedNumber === 60 ? (hours === 23 ? 0 : ++hours) : hours;
  }

  return { roundedHours: +timePad(h), roundedMinutes: +timePad(m) };
};

const timePad = (time: number) => {
  return time.toString().padStart(2, '0');
};

export const detectMedia = (callback: any) => {
  const media = navigator.mediaDevices;
  if (!media || !media.enumerateDevices) return callback(false);
  media.getUserMedia({ video: true }).then(
    () => {
      media.enumerateDevices().then((devices: any) => {
        callback(devices.some((device: any) => 'videoinput' === device.kind));
      });
    },
    () => {
      toaster.info({
        description: 'Cannot get camera access! The device doesnt have a camera or camera permission is denied!',
      });
      callback(false);
    }
  );
};

export const getUserLocation = (onSuccess: (position: GeolocationPosition) => void) => {
  if (!navigator.geolocation) {
    toaster.error({ description: 'Browser does not support Geo Location!' });
  } else {
    navigator.geolocation.getCurrentPosition(onSuccess, () => {
      toaster.error({ description: 'Cannot access user location. Please allow location access!' });
    });
  }
};

export const formatDistance = (distInMeters: number, isKm: boolean): number => {
  if (isKm) {
    return distInMeters / 1000;
  } else {
    return distInMeters * 0.000621371;
  }
};
